import React, {useEffect, useState, useMemo, useRef} from 'react';
import styled from 'styled-components';
import moment from 'moment';
import {PuffLoader} from 'react-spinners';
import useFetchData from '../../hooks/useFetchData';
import {TableWrapper} from '../../components/TableWrapper';
import {ColumnsType} from 'antd/lib/table';
import {getNoun} from '../../utils/functions/getNoun';

import type {DatePickerProps} from 'antd';
import {DatePicker} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import type {InputRef, TableColumnType} from 'antd';
import {Button, Input, Space} from 'antd';
import type {FilterDropdownProps} from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';

import history from '../../hystory';
import {useApplication} from '../../context/ApplicationContext';
import dayjs from 'dayjs';
import {useSelector} from 'react-redux';
import {authInfoSelect} from '../../entities/auth';
import {
    API_DATE_FORMAT,
    cssOverride,
    DISPLAY_DATE_FORMAT,
} from '../../utils/const';

moment().format();

interface DataType {
    goodName: string;
    groupId: number;
    mUnitsName: string;
    mUnitsRid: number;
    maxReserve: number;
    minReserve: number;
    quantity: number;
    rid: number;
    sumNds: number;
    sumNsp: number;
    sumWithoutTax: number;
}

type DataIndex = keyof DataType;

const OstatkiPage = () => {
    const {dateFilterMain, setDateFilterMain} = useApplication();
    const {pathname, search} = window.location;
    const searchParams = new URLSearchParams(search);
    const {shop} = useSelector(authInfoSelect);

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [datePickerValue, setDatePickerValue] = useState<any>(null);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const urlForRemains = useMemo(() => {
        const newURI = datePickerValue
            ? `${process.env.REACT_APP_API_URL}/shops/${shop?.id}/remains?date=${datePickerValue}`
            : `${process.env.REACT_APP_API_URL}/shops/${shop?.id}/remains`;

        return newURI;
    }, [datePickerValue, shop]);

    const {
        data: goodsData,
        loading,
        setParams: setHeaderParams,
    } = useFetchData(urlForRemains, [urlForRemains]);

    const disabledDate: DatePickerProps['disabledDate'] = (current) => {
        return current && current > dayjs().endOf('day');
    };

    useEffect(() => {
        setDatePickerValue(dateFilterMain);
    }, [dateFilterMain]);

    const onDateChange: DatePickerProps['onChange'] = (date, dateString) => {
        if (date) {
            const apiFormatDate = date.format(API_DATE_FORMAT);
            searchParams.set('date', apiFormatDate);
            setDatePickerValue(apiFormatDate);
            setHeaderParams([{url: urlForRemains}]);
            setDateFilterMain(apiFormatDate);
            history?.replace({
                pathname,
                search: searchParams.toString(),
            });
        }
    };

    const getColumnSearchProps = (
        dataIndex: DataIndex
    ): TableColumnType<DataType> => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            close,
        }) => (
            <div style={{padding: 8}} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() =>
                        handleSearch(
                            selectedKeys as string[],
                            confirm,
                            dataIndex
                        )
                    }
                    style={{marginBottom: 8, display: 'block'}}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() =>
                            handleSearch(
                                selectedKeys as string[],
                                confirm,
                                dataIndex
                            )
                        }
                        icon={<SearchOutlined />}
                        size="small"
                        style={{width: 90}}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() =>
                            clearFilters && handleReset(clearFilters)
                        }
                        size="small"
                        style={{width: 90}}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({closeDropdown: false});
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType = [
        {
            title: 'Наименование',
            dataIndex: 'goodName',
            render: (item_name, record) => {
                return <span>{item_name}</span>;
            },
            width: 100,
            // sorter: (a: DataType, b: DataType) => a.goodName && b.goodName ? a.goodName.localeCompare(b.goodName) : 0,
            ...getColumnSearchProps('goodName'),
        },
        {
            title: 'Ед.изм',
            dataIndex: 'mUnitsName',
            align: 'right',
            render: (shops_counter: string) => {
                return <span>{shops_counter}</span>;
            },
            width: 100,
            // sorter: (a: DataType, b: DataType) => a.mUnitsName && b.mUnitsName ? a.mUnitsName.localeCompare(b.mUnitsName) : 0,
        },
        {
            title: 'Остаток',
            dataIndex: 'quantity',
            align: 'right',
            render: (shops_counter: number) => {
                return <StyledSum>{shops_counter}</StyledSum>;
            },
            width: 100,
            // sorter: (a: DataType, b: DataType) => a.quantity - b.quantity,
        },
        {
            title: 'Сумма без налогов',
            dataIndex: 'sumWithoutTax',
            align: 'right',
            render: (shops_counter: number) => {
                return (
                    <StyledSum>
                        {new Intl.NumberFormat('ru-RU').format(
                            Number(shops_counter)
                        )}{' '}
                        р.
                    </StyledSum>
                );
            },
            width: 100,
            // sorter: (a, b) => a.sumWithoutTax - b.sumWithoutTax,
        },
    ];

    return (
        <Wrapper>
            <Header>
                <Title>
                    Товары
                    <SubTitle>
                        {goodsData && Array.isArray(goodsData)
                            ? getNoun(
                                  goodsData.length,
                                  'товар',
                                  'товара',
                                  'товаров'
                              )
                            : 'нет товаров'}
                    </SubTitle>
                </Title>
                <StyledCaledarWrapper>
                    <DatePicker
                        onChange={onDateChange}
                        value={
                            datePickerValue
                                ? dayjs(datePickerValue, API_DATE_FORMAT)
                                : null
                        }
                        format={DISPLAY_DATE_FORMAT}
                        disabledDate={disabledDate}
                    />
                </StyledCaledarWrapper>
            </Header>
            <div>
                {loading ? (
                    <PuffLoader color="#E65E20" cssOverride={cssOverride} />
                ) : (
                    <TableWrapper
                        data={goodsData}
                        loading={loading}
                        columns={columns}
                    />
                )}
            </div>
        </Wrapper>
    );
};

export default OstatkiPage;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    /* min-width: 100%; */
    width: 100%;
    min-height: 80%;
    top: 0px;
    background-color: #fff;
    padding: 16px 0px 16px 128px;

    .ant-table-thead > tr > th {
        background-color: #9481cd40;
        font-family: 'Inter';
        font-size: 14px;
        font-weight: 600;
        line-height: 16.8px;
        text-align: left;
    }

    .ant-table-footer {
        background-color: #9481cd40;
        font-family: 'Inter';
        font-size: 14px;
        font-weight: 600;
        line-height: 16.8px;
        text-align: left;
    }

    table > tbody > tr > td {
        background-color: #ffffff;
        font-family: Inter;
        border: 1px solid #efefef;
        color: #333333;
        font-size: 14px;
        font-weight: 400;
        line-height: 16.8px;
        text-align: left;
        height: 30px;
    }
`;

const Header = styled.div`
    width: 100%;
    height: 40px;
    display: flex;
    justify-content: space-between;
    top: 0px;
    margin-bottom: 20px;
    /* gap: 30px; */
`;

const Title = styled.div`
    display: flex;
    flex-direction: column;
    font-weight: 600;
    font-size: 18px;
    line-height: 26px;
    letter-spacing: 0.02em;
    color: #000000;
`;

const SubTitle = styled.span`
    font-weight: 500;
    font-size: 13px;
    line-height: 26px;
    letter-spacing: 0.02em;
    color: #999999;
`;

const StyledSum = styled.div`
    text-align: right;
    padding-right: 0.5em;
`;

const StyledCaledarWrapper = styled.div`
    width: 250px;
    color: #ffffff;
    display: flex;
    gap: 10px;
    color: #ffffff;

    & svg {
        width: 20px;
        height: 20px;
    }
    .ant-picker {
        background-color: #a7388e;
        font-family: Inter;
        font-size: 14px;
        font-weight: 400;
        line-height: 28px;
        text-align: center;
        color: #ffffff;
    }

    .ant-picker .ant-picker-input,
    .ant-picker .ant-picker-range,
    .ant-picker .ant-picker-input-active .ant-picker-input-placeholder {
        display: flex;
        flex-direction: row-reverse;
        font-family: 'Inter';
        font-size: 14px;
        font-weight: 400;
        line-height: 28px;
        color: #ffffff;
    }

    .ant-picker .ant-picker-input .ant-picker-input-active {
        color: #ffffff !important;
    }

    .ant-picker .ant-picker-input .ant-picker-input-placeholder {
        color: #ffffff !important;
    }

    .ant-picker .ant-picker-input span.ant-picker-suffix,
    .ant-picker.ant-picker-range span.ant-picker-suffix {
        order: -1;
        margin-left: 0;
        margin-right: 10px; /* Для отступа иконки от текста */
        color: #ffffff;
    }

    .ant-picker.ant-picker-range.ant-picker-range-separator {
        color: #ffffff !important;
        /* display: none; */
    }

    .ant-picker-range-separator > span {
        color: #ffffff !important; /* Замените #ваш_цвет на нужный код цвета */
    }

    .ant-picker-clear {
        color: #ffffff !important; /* Замените #ваш_цвет на нужный код цвета */
    }

    .ant-picker .ant-picker-clear {
        position: absolute;
        right: 9px;
        /* color: #ffffff !important; */
    }
`;
